import { ArrowLeft, Calendar, Clock, User, CheckCircle, Gift, Heart, Share2, MessageCircle, Star, Zap, Smile, Briefcase, Home, Baby, AlertTriangle, Lightbulb, Sparkles } from 'lucide-react';
import { useTranslation } from '../utils/useTranslation';

interface BlogPost3Props {
    onBack: () => void;
}

export function BlogPost3({ onBack }: BlogPost3Props) {
    const { t, tObject } = useTranslation();

    const meta = tObject('blogPost3.meta') as any;
    const intro = tObject('blogPost3.intro') as any;
    const theme1 = tObject('blogPost3.theme1') as any;
    const theme2 = tObject('blogPost3.theme2') as any;
    const theme3 = tObject('blogPost3.theme3') as any;
    const theme4 = tObject('blogPost3.theme4') as any;
    const theme5 = tObject('blogPost3.theme5') as any;
    const cta = tObject('blogPost3.cta') as any;

    return (
        <div className="min-h-screen bg-white pt-24 pb-12">
            {/* Hero Section */}
            <div className="relative h-[600px] w-full overflow-hidden mb-12">
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-500 to-orange-600 opacity-90"></div>
                <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1513201099705-a9746e1e201f?auto=format&fit=crop&q=80')] bg-cover bg-center mix-blend-overlay"></div>
                <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4 text-center">
                    <span className="px-4 py-1 bg-white/20 backdrop-blur-md rounded-full text-sm font-medium mb-6 border border-white/30">
                        {meta?.badge}
                    </span>
                    <h1 className="text-4xl md:text-6xl font-bold max-w-5xl mb-6 leading-tight drop-shadow-lg">
                        {meta?.title}
                    </h1>
                    <div className="flex flex-wrap items-center justify-center gap-6 text-sm md:text-base opacity-90">
                        <div className="flex items-center gap-2">
                            <User className="w-5 h-5" />
                            <span>{meta?.author}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            <span>{meta?.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <Clock className="w-5 h-5" />
                            <span>{meta?.readTime}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container mx-auto px-4 max-w-4xl">
                <button
                    onClick={onBack}
                    className="group flex items-center gap-2 text-gray-600 hover:text-orange-600 transition-colors mb-8 font-medium"
                >
                    <div className="p-2 bg-gray-100 rounded-full group-hover:bg-orange-50 transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </div>
                    {meta?.backButton}
                </button>

                {/* Content Container */}
                <article className="prose prose-lg max-w-none">
                    <p className="lead text-2xl text-gray-600 mb-10 font-light leading-relaxed border-l-4 border-orange-500 pl-6 italic">
                        {intro?.lead}
                    </p>

                    <div className="bg-orange-50 rounded-2xl p-8 mb-12 border border-orange-100">
                        <h3 className="text-orange-800 font-bold text-xl mb-4 flex items-center gap-2">
                            <Star className="w-5 h-5 fill-current" />
                            {intro?.roadmapTitle}
                        </h3>
                        <div className="grid md:grid-cols-2 gap-4">
                            <ul className="list-none space-y-3 m-0">
                                <li className="flex items-center gap-2 text-gray-700">
                                    <Zap className="w-4 h-4 text-yellow-500" />
                                    <strong>{intro?.roadmap?.unusual?.split(':')[0]}:</strong> {intro?.roadmap?.unusual?.split(':').slice(1).join(':')}
                                </li>
                                <li className="flex items-center gap-2 text-gray-700">
                                    <Smile className="w-4 h-4 text-green-500" />
                                    <strong>{intro?.roadmap?.funny?.split(':')[0]}:</strong> {intro?.roadmap?.funny?.split(':').slice(1).join(':')}
                                </li>
                                <li className="flex items-center gap-2 text-gray-700">
                                    <Briefcase className="w-4 h-4 text-blue-500" />
                                    <strong>{intro?.roadmap?.corporate?.split(':')[0]}:</strong> {intro?.roadmap?.corporate?.split(':').slice(1).join(':')}
                                </li>
                            </ul>
                            <ul className="list-none space-y-3 m-0">
                                <li className="flex items-center gap-2 text-gray-700">
                                    <Home className="w-4 h-4 text-red-500" />
                                    <strong>{intro?.roadmap?.family?.split(':')[0]}:</strong> {intro?.roadmap?.family?.split(':').slice(1).join(':')}
                                </li>
                                <li className="flex items-center gap-2 text-gray-700">
                                    <Baby className="w-4 h-4 text-pink-500" />
                                    <strong>{intro?.roadmap?.kids?.split(':')[0]}:</strong> {intro?.roadmap?.kids?.split(':').slice(1).join(':')}
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* Tópico 1: Amigo Secreto Inusitado */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-yellow-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-yellow-500 text-white rounded-xl text-2xl font-bold">1</span>
                            {theme1?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{theme1?.intro}</p>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{theme1?.themesTitle}</h3>
                        <p className="text-gray-700 mb-4">{theme1?.themesIntro}</p>
                        <ul className="space-y-6 mb-8">
                            <li className="bg-yellow-50 p-6 rounded-xl border border-yellow-100 hover:shadow-md transition-shadow">
                                <strong className="text-xl text-yellow-800 block mb-3 flex items-center gap-2">
                                    {theme1?.ideas?.recycle?.title}
                                </strong>
                                <p className="text-gray-700 m-0 leading-relaxed">{theme1?.ideas?.recycle?.description}</p>
                            </li>
                            <li className="bg-yellow-50 p-6 rounded-xl border border-yellow-100 hover:shadow-md transition-shadow">
                                <strong className="text-xl text-yellow-800 block mb-3 flex items-center gap-2">
                                    {theme1?.ideas?.letter?.title}
                                </strong>
                                <p className="text-gray-700 m-0 leading-relaxed">{theme1?.ideas?.letter?.description}</p>
                            </li>
                            <li className="bg-yellow-50 p-6 rounded-xl border border-yellow-100 hover:shadow-md transition-shadow">
                                <strong className="text-xl text-yellow-800 block mb-3 flex items-center gap-2">
                                    {theme1?.ideas?.havaianas?.title}
                                </strong>
                                <p className="text-gray-700 m-0 leading-relaxed">{theme1?.ideas?.havaianas?.description}</p>
                            </li>
                        </ul>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{theme1?.challengesTitle}</h3>
                        <p className="text-gray-700 mb-6">{theme1?.challengesIntro}</p>
                        <div className="grid md:grid-cols-2 gap-6 mb-8">
                            <div className="border-2 border-gray-100 p-6 rounded-xl hover:border-yellow-200 transition-colors bg-white">
                                <h4 className="font-bold text-gray-900 mb-4 text-lg border-b pb-2">{theme1?.lightChallenges?.title}</h4>
                                <ul className="list-disc pl-5 text-gray-600 space-y-2">
                                    {theme1?.lightChallenges?.items?.map((item: string, i: number) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className="border-2 border-gray-100 p-6 rounded-xl hover:border-yellow-200 transition-colors bg-white">
                                <h4 className="font-bold text-gray-900 mb-4 text-lg border-b pb-2">{theme1?.creativeChallenges?.title}</h4>
                                <ul className="list-disc pl-5 text-gray-600 space-y-2">
                                    {theme1?.creativeChallenges?.items?.map((item: string, i: number) => (
                                        <li key={i}>{item}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </section>

                    {/* Tópico 2: Amigo da Onça */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-green-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-green-600 text-white rounded-xl text-2xl font-bold">2</span>
                            {theme2?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{theme2?.intro}</p>

                        <div className="bg-red-50 border-l-8 border-red-500 p-8 mb-8 rounded-r-xl shadow-sm">
                            <h4 className="font-bold text-red-900 mb-3 flex items-center gap-3 text-xl">
                                <AlertTriangle className="w-6 h-6" />
                                {theme2?.goldenRule?.title}
                            </h4>
                            <p className="text-gray-800 m-0 leading-relaxed text-lg">{theme2?.goldenRule?.content}</p>
                        </div>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{theme2?.trollIdeasTitle}</h3>
                        <p className="text-gray-700 mb-6">{theme2?.trollIdeasIntro}</p>
                        <ul className="grid md:grid-cols-2 gap-6 mb-8">
                            <li className="flex gap-4 items-start bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                                <div className="text-4xl bg-gray-100 p-3 rounded-full">🧱</div>
                                <div>
                                    <strong className="text-gray-900 block text-lg mb-1">{theme2?.trollIdeas?.brick?.title}</strong>
                                    <p className="text-gray-600 text-sm">{theme2?.trollIdeas?.brick?.description}</p>
                                </div>
                            </li>
                            <li className="flex gap-4 items-start bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                                <div className="text-4xl bg-gray-100 p-3 rounded-full">🧦</div>
                                <div>
                                    <strong className="text-gray-900 block text-lg mb-1">{theme2?.trollIdeas?.kit?.title}</strong>
                                    <p className="text-gray-600 text-sm">{theme2?.trollIdeas?.kit?.description}</p>
                                </div>
                            </li>
                            <li className="flex gap-4 items-start bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                                <div className="text-4xl bg-gray-100 p-3 rounded-full">🐔</div>
                                <div>
                                    <strong className="text-gray-900 block text-lg mb-1">{theme2?.trollIdeas?.chicken?.title}</strong>
                                    <p className="text-gray-600 text-sm">{theme2?.trollIdeas?.chicken?.description}</p>
                                </div>
                            </li>
                            <li className="flex gap-4 items-start bg-white p-4 rounded-xl shadow-sm border border-gray-100">
                                <div className="text-4xl bg-gray-100 p-3 rounded-full">🧩</div>
                                <div>
                                    <strong className="text-gray-900 block text-lg mb-1">{theme2?.trollIdeas?.puzzle?.title}</strong>
                                    <p className="text-gray-600 text-sm">{theme2?.trollIdeas?.puzzle?.description}</p>
                                </div>
                            </li>
                        </ul>
                    </section>

                    {/* Tópico 3: Amigo Secreto do Trabalho */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-blue-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-blue-600 text-white rounded-xl text-2xl font-bold">3</span>
                            {theme3?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{theme3?.intro}</p>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{theme3?.survivalTitle}</h3>
                        <div className="space-y-8">
                            <div className="bg-red-50 p-6 rounded-xl border border-red-100">
                                <h4 className="font-bold text-red-800 text-lg mb-3 flex items-center gap-2">{theme3?.dontGive?.title}</h4>
                                <p className="text-gray-700 leading-relaxed">{theme3?.dontGive?.content}</p>
                            </div>
                            <div className="bg-green-50 p-6 rounded-xl border border-green-100">
                                <h4 className="font-bold text-green-800 text-lg mb-3 flex items-center gap-2">{theme3?.safeGifts?.title}</h4>
                                <ul className="list-disc pl-6 text-gray-700 space-y-3 leading-relaxed">
                                    <li><strong>{theme3?.safeGifts?.stationery?.split(':')[0]}:</strong> {theme3?.safeGifts?.stationery?.split(':').slice(1).join(':')}</li>
                                    <li><strong>{theme3?.safeGifts?.books?.split(':')[0]}:</strong> {theme3?.safeGifts?.books?.split(':').slice(1).join(':')}</li>
                                    <li><strong>{theme3?.safeGifts?.giftCards?.split(':')[0]}:</strong> {theme3?.safeGifts?.giftCards?.split(':').slice(1).join(':')}</li>
                                    <li><strong>{theme3?.safeGifts?.office?.split(':')[0]}:</strong> {theme3?.safeGifts?.office?.split(':').slice(1).join(':')}</li>
                                </ul>
                            </div>
                            <div className="bg-blue-50 p-6 rounded-xl border border-blue-100">
                                <h4 className="font-bold text-blue-800 text-lg mb-3 flex items-center gap-2">{theme3?.techTip?.title}</h4>
                                <p className="text-gray-700 leading-relaxed">{theme3?.techTip?.content}</p>
                            </div>
                        </div>
                    </section>

                    {/* Tópico 4: Amigo Secreto de Família */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-red-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-red-600 text-white rounded-xl text-2xl font-bold">4</span>
                            {theme4?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{theme4?.intro}</p>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{theme4?.strategiesTitle}</h3>

                        <div className="bg-white shadow-lg rounded-xl p-8 mb-8 border border-gray-100 hover:shadow-xl transition-shadow">
                            <h4 className="font-bold text-xl mb-4 flex items-center gap-3 text-red-600">
                                <Lightbulb className="w-6 h-6" />
                                {theme4?.sharedWishlist?.title}
                            </h4>
                            <p className="text-gray-700 mb-4 leading-relaxed">{theme4?.sharedWishlist?.content}</p>
                        </div>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{theme4?.budgetIdeasTitle}</h3>
                        <p className="text-gray-700 mb-6">{theme4?.budgetIdeasIntro}</p>
                        <div className="grid md:grid-cols-3 gap-6 mb-8">
                            <div className="bg-red-50 p-6 rounded-2xl text-center hover:bg-red-100 transition-colors">
                                <span className="text-4xl mb-4 block transform hover:scale-110 transition-transform">🍪</span>
                                <strong className="block text-gray-900 text-lg mb-2">{theme4?.budgetIdeas?.treats?.title}</strong>
                                <p className="text-sm text-gray-600 leading-relaxed">{theme4?.budgetIdeas?.treats?.description}</p>
                            </div>
                            <div className="bg-red-50 p-6 rounded-2xl text-center hover:bg-red-100 transition-colors">
                                <span className="text-4xl mb-4 block transform hover:scale-110 transition-transform">🖼️</span>
                                <strong className="block text-gray-900 text-lg mb-2">{theme4?.budgetIdeas?.frame?.title}</strong>
                                <p className="text-sm text-gray-600 leading-relaxed">{theme4?.budgetIdeas?.frame?.description}</p>
                            </div>
                            <div className="bg-red-50 p-6 rounded-2xl text-center hover:bg-red-100 transition-colors">
                                <span className="text-4xl mb-4 block transform hover:scale-110 transition-transform">🌿</span>
                                <strong className="block text-gray-900 text-lg mb-2">{theme4?.budgetIdeas?.plant?.title}</strong>
                                <p className="text-sm text-gray-600 leading-relaxed">{theme4?.budgetIdeas?.plant?.description}</p>
                            </div>
                        </div>
                    </section>

                    {/* Tópico 5: Amigo Secreto Infantil */}
                    <section className="mb-16">
                        <h2 className="text-4xl font-bold text-gray-800 mt-12 mb-6 flex items-center gap-3 border-b-2 border-pink-200 pb-4">
                            <span className="flex items-center justify-center w-12 h-12 bg-pink-500 text-white rounded-xl text-2xl font-bold">5</span>
                            {theme5?.title}
                        </h2>
                        <p className="text-gray-700 mb-6">{theme5?.intro}</p>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{theme5?.tipsTitle}</h3>
                        <ul className="list-disc pl-6 text-gray-700 space-y-4 mb-8">
                            <li>
                                <strong>{theme5?.tips?.ageGroup?.split(':')[0]}:</strong> {theme5?.tips?.ageGroup?.split(':').slice(1).join(':')}
                            </li>
                            <li>
                                <strong>{theme5?.tips?.fixedValue?.split(':')[0]}:</strong> {theme5?.tips?.fixedValue?.split(':').slice(1).join(':')}
                            </li>
                            <li>
                                <strong>{theme5?.tips?.directed?.split(':')[0]}:</strong> {theme5?.tips?.directed?.split(':').slice(1).join(':')}
                            </li>
                        </ul>

                        <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">{theme5?.giftIdeasTitle}</h3>
                        <p className="text-gray-700 mb-6">{theme5?.giftIdeasIntro}</p>
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="flex gap-5 items-start bg-pink-50 p-4 rounded-xl">
                                <div className="bg-pink-200 w-10 h-10 flex items-center justify-center rounded-full text-pink-700 font-bold flex-shrink-0">A</div>
                                <div>
                                    <strong className="text-gray-900 block mb-1">{theme5?.giftIdeas?.books?.title}</strong>
                                    <p className="text-gray-600 text-sm">{theme5?.giftIdeas?.books?.description}</p>
                                </div>
                            </div>
                            <div className="flex gap-5 items-start bg-pink-50 p-4 rounded-xl">
                                <div className="bg-pink-200 w-10 h-10 flex items-center justify-center rounded-full text-pink-700 font-bold flex-shrink-0">B</div>
                                <div>
                                    <strong className="text-gray-900 block mb-1">{theme5?.giftIdeas?.art?.title}</strong>
                                    <p className="text-gray-600 text-sm">{theme5?.giftIdeas?.art?.description}</p>
                                </div>
                            </div>
                            <div className="flex gap-5 items-start bg-pink-50 p-4 rounded-xl">
                                <div className="bg-pink-200 w-10 h-10 flex items-center justify-center rounded-full text-pink-700 font-bold flex-shrink-0">C</div>
                                <div>
                                    <strong className="text-gray-900 block mb-1">{theme5?.giftIdeas?.games?.title}</strong>
                                    <p className="text-gray-600 text-sm">{theme5?.giftIdeas?.games?.description}</p>
                                </div>
                            </div>
                            <div className="flex gap-5 items-start bg-pink-50 p-4 rounded-xl">
                                <div className="bg-pink-200 w-10 h-10 flex items-center justify-center rounded-full text-pink-700 font-bold flex-shrink-0">D</div>
                                <div>
                                    <strong className="text-gray-900 block mb-1">{theme5?.giftIdeas?.bubbles?.title}</strong>
                                    <p className="text-gray-600 text-sm">{theme5?.giftIdeas?.bubbles?.description}</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <hr className="my-12 border-gray-200" />

                    {/* Conclusão e CTA */}
                    <div className="bg-gradient-to-r from-yellow-500 to-orange-600 rounded-3xl p-8 md:p-16 text-white text-center shadow-2xl relative overflow-hidden transform transition-all hover:scale-[1.01]">
                        <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
                        <Sparkles className="w-24 h-24 mx-auto mb-8 text-yellow-200 animate-pulse drop-shadow-md" />
                        <h2 className="text-3xl md:text-5xl font-bold mb-8 relative z-10 leading-tight">{cta?.title}</h2>
                        <p className="text-xl opacity-90 mb-12 max-w-3xl mx-auto leading-relaxed relative z-10 font-light">
                            {cta?.description}
                        </p>
                        <button
                            onClick={onBack}
                            className="bg-white text-orange-600 px-12 py-6 rounded-full font-bold text-xl hover:bg-yellow-50 hover:shadow-2xl hover:-translate-y-1 transition-all shadow-xl relative z-10 flex items-center gap-4 mx-auto group"
                        >
                            <Gift className="w-7 h-7 group-hover:rotate-12 transition-transform" />
                            {cta?.button}
                        </button>
                    </div>
                </article>
            </div>
        </div>
    );
}